// import React, { useState, useEffect, useCallback } from 'react';
// import {
//     Box,
//     Typography,
//     Table,
//     TableBody,
//     TableCell,
//     TableContainer,
//     TableHead,
//     TableRow,
//     Paper,
//     TextField,
//     InputAdornment,
//     MenuItem,
//     Select,
//     FormControl,
//     InputLabel,
//     SelectChangeEvent,
//     Chip,
//     CircularProgress,
//     Alert,
//     Card,
//     CardContent,
// } from '@mui/material';
// import { Search, Receipt, Payment, Assignment, RequestQuote, FiberManualRecord, CalendarToday } from '@mui/icons-material';
// import { format } from 'date-fns';
// import { request } from '../../utils/request';
// import { useDebounce } from '../../utils/useDebounce';
// import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// // Types for combined transactions with running balance
// interface BaseTransaction {
//     id: string;
//     date: string;
//     documentType: 'SALES_INVOICE' | 'PURCHASE' | 'PAYMENT_IN' | 'PAYMENT_OUT' | 'SALES_RETURN' | 'PURCHASE_RETURN' | 'CREDIT_NOTE' | 'DEBIT_NOTE';
//     documentNumber: string;
//     amount: number;
//     status?: string;
//     paymentType?: string;
//     receivedAmount?: number;
//     paidAmount?: number;
//     totalAmount?: number;
//     runningBalance?: number; // Added for running balance
// }

// interface SalesInvoice extends BaseTransaction {
//     documentType: 'SALES_INVOICE';
//     totalAmount: number;
//     receivedAmount: number;
//     balanceAmount: number;
// }

// interface PurchaseInvoice extends BaseTransaction {
//     documentType: 'PURCHASE';
//     totalAmount: number;
//     paidAmount: number;
//     balanceAmount: number;
// }

// interface PaymentIn extends BaseTransaction {
//     documentType: 'PAYMENT_IN';
//     amount: number;
//     paymentType: string;
// }

// interface PaymentOut extends BaseTransaction {
//     documentType: 'PAYMENT_OUT';
//     amount: number;
//     paymentType: string;
// }

// interface SalesReturn extends BaseTransaction {
//     documentType: 'SALES_RETURN';
//     totalAmount: number;
// }

// interface PurchaseReturn extends BaseTransaction {
//     documentType: 'PURCHASE_RETURN';
//     totalAmount: number;
// }

// interface CreditNote extends BaseTransaction {
//     documentType: 'CREDIT_NOTE';
//     totalAmount: number;
// }

// interface DebitNote extends BaseTransaction {
//     documentType: 'DEBIT_NOTE';
//     totalAmount: number;
// }

// type Transaction = SalesInvoice | PurchaseInvoice | PaymentIn | PaymentOut | SalesReturn | PurchaseReturn | CreditNote | DebitNote;

// type TransactionWithBalance = Transaction & {
//     runningBalance: number;
// };

// interface LedgerTableProps {
//     entityId: string;
//     entityType: 'party' | 'vendor';
//     gstType?: 'GST' | 'NON-GST';
//     entityName?: string;
//     entityContact?: string;
//     entityLocation?: string;
//     ledgerLabel?: string;
//     onTotalChange?: (totalDebit: number, totalCredit: number, balance: number) => void;
// }

// const CommonLedgerTable: React.FC<LedgerTableProps> = ({
//     entityId,
//     entityType,
//     gstType = 'GST',
//     entityName,
//     entityContact,
//     entityLocation,
//     ledgerLabel,
//     onTotalChange
// }) => {
//     const [transactions, setTransactions] = useState<Transaction[]>([]);
//     const [filteredTransactions, setFilteredTransactions] = useState<TransactionWithBalance[]>([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState('');

//     // Filter states
//     const [searchTerm, setSearchTerm] = useState('');
//     const debouncedSearch = useDebounce(searchTerm, 500);
//     const [dateRange, setDateRange] = useState<string>('all');
//     const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
//     const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

//     // Totals
//     const [totals, setTotals] = useState({
//         totalDebit: 0,
//         totalCredit: 0,
//         balance: 0
//     });

//     // Get API endpoints based on entity type
//     const getApiEndpoints = useCallback(() => {
//         if (entityType === 'party') {
//             return {
//                 invoices: 'invoice/get-all-sales-invoices',
//                 payments: 'paymentIn/get-all-payment-ins',
//                 returns: 'salesReturn/get-all-sales-returns',
//                 notes: 'creditNote/get-all-credit-notes'
//             };
//         } else {
//             return {
//                 invoices: 'purchase/get-all-purchase-invoices',
//                 payments: 'paymentOut/get-all-payment-outs',
//                 returns: 'purchaseReturn/get-all-purchase-returns',
//                 notes: 'debitNote/get-all-debit-notes'
//             };
//         }
//     }, [entityType]);

//     const getDebitAmount = useCallback((transaction: Transaction): number => {
//         if (entityType === 'party') {
//             switch (transaction.documentType) {
//                 case 'SALES_INVOICE':
//                     return (transaction as SalesInvoice).totalAmount || 0;
//                 default:
//                     return 0;
//             }
//         } else {
//             switch (transaction.documentType) {
//                 case 'PAYMENT_OUT':
//                     return (transaction as PaymentOut).amount || 0;
//                 case 'PURCHASE_RETURN':
//                 case 'DEBIT_NOTE':
//                     return (transaction as PurchaseReturn | DebitNote).totalAmount || 0;
//                 default:
//                     return 0;
//             }
//         }
//     }, [entityType])

//     const getCreditAmount = useCallback((transaction: Transaction): number => {
//         if (entityType === 'party') {
//             switch (transaction.documentType) {
//                 case 'SALES_INVOICE':
//                     return (transaction as SalesInvoice).receivedAmount || 0;
//                 case 'PAYMENT_IN':
//                     return (transaction as PaymentIn).amount || 0;
//                 case 'SALES_RETURN':
//                 case 'CREDIT_NOTE':
//                     return (transaction as SalesReturn | CreditNote).totalAmount || 0;
//                 default:
//                     return 0;
//             }
//         } else {
//             switch (transaction.documentType) {
//                 case 'PURCHASE':
//                     return (transaction as PurchaseInvoice).totalAmount || 0;
//                 default:
//                     return 0;
//             }
//         }
//     }, [entityType])

//     // Calculate running balance
//     const calculateRunningBalance = useCallback((transactions: Transaction[]): TransactionWithBalance[] => {
//         let balance = 0;

//         // Sort by date (oldest first) for running balance calculation
//         const sorted = [...transactions].sort((a, b) =>
//             new Date(a.date).getTime() - new Date(b.date).getTime()
//         );

//         return sorted.map(transaction => {
//             if (entityType === 'party') {
//                 // For parties: Debit increases balance, Credit decreases balance
//                 const debitAmount = getDebitAmount(transaction);
//                 const creditAmount = getCreditAmount(transaction);
//                 balance = balance + debitAmount - creditAmount;
//             } else {
//                 // For vendors: Credit increases balance (we owe), Debit decreases balance (we paid)
//                 const debitAmount = getDebitAmount(transaction);
//                 const creditAmount = getCreditAmount(transaction);
//                 balance = balance + creditAmount - debitAmount;
//             }

//             return {
//                 ...transaction,
//                 runningBalance: balance
//             };
//         }); // Reverse back to newest first for display
//     }, [entityType, getDebitAmount, getCreditAmount]);

//     // Calculate totals
//     const calculateTotals = useCallback((data: Transaction[]) => {
//         let totalDebit = 0;
//         let totalCredit = 0;

//         data.forEach(t => {
//             if (entityType === 'party') {
//                 // For parties (customer perspective)
//                 switch (t.documentType) {
//                     case 'SALES_INVOICE':
//                         totalDebit += (t as SalesInvoice).totalAmount || 0;
//                         totalCredit += (t as SalesInvoice).receivedAmount || 0;
//                         break;
//                     case 'PAYMENT_IN':
//                         totalCredit += (t as PaymentIn).amount || 0;
//                         break;
//                     case 'SALES_RETURN':
//                     case 'CREDIT_NOTE':
//                         totalCredit += (t as SalesReturn | CreditNote).totalAmount || 0;
//                         break;
//                 }
//             } else {
//                 // For vendors (supplier perspective)
//                 switch (t.documentType) {
//                     case 'PURCHASE':
//                         totalCredit += (t as PurchaseInvoice).totalAmount || 0;
//                         totalDebit += (t as PurchaseInvoice).paidAmount || 0;
//                         break;
//                     case 'PAYMENT_OUT':
//                         totalDebit += (t as PaymentOut).amount || 0;
//                         break;
//                     case 'PURCHASE_RETURN':
//                     case 'DEBIT_NOTE':
//                         totalDebit += (t as PurchaseReturn | DebitNote).totalAmount || 0;
//                         break;
//                 }
//             }
//         });

//         const balance = entityType === 'party'
//             ? totalDebit - totalCredit
//             : totalCredit - totalDebit;

//         setTotals({ totalDebit, totalCredit, balance });

//         if (onTotalChange) {
//             onTotalChange(totalDebit, totalCredit, balance);
//         }
//     }, [entityType, onTotalChange])

//     // Apply filters (search and date range) and calculate running balance
//     const applyFilters = useCallback((data: Transaction[]) => {
//         let filtered = [...data];

//         // Apply search filter
//         if (debouncedSearch) {
//             const search = debouncedSearch.toLowerCase();
//             filtered = filtered.filter(t =>
//                 t.documentNumber?.toLowerCase().includes(search) ||
//                 t.documentType.toLowerCase().includes(search)
//             );
//         }

//         // Apply date range filter
//         if (dateRange !== 'all') {
//             const now = new Date();
//             let startDate = new Date();

//             if (dateRange === 'custom' && customStartDate && customEndDate) {
//                 startDate = new Date(customStartDate);
//                 startDate.setHours(0, 0, 0, 0);
//                 const endDate = new Date(customEndDate);
//                 endDate.setHours(23, 59, 59, 999);

//                 filtered = filtered.filter(t => {
//                     const tDate = new Date(t.date);
//                     return tDate >= startDate && tDate <= endDate;
//                 });
//             } else {
//                 switch (dateRange) {
//                     case '3months':
//                         startDate.setMonth(now.getMonth() - 3);
//                         break;
//                     case '6months':
//                         startDate.setMonth(now.getMonth() - 6);
//                         break;
//                     case '1year':
//                         startDate.setFullYear(now.getFullYear() - 1);
//                         break;
//                     default:
//                         break;
//                 }

//                 if (dateRange !== 'all' && dateRange !== 'custom') {
//                     filtered = filtered.filter(t => new Date(t.date) >= startDate);
//                 }
//             }
//         }

//         // Calculate running balance
//         const withBalance = calculateRunningBalance(filtered);
//         setFilteredTransactions(withBalance);
//         calculateTotals(filtered);
//     }, [debouncedSearch, dateRange, customStartDate, customEndDate, calculateRunningBalance, calculateTotals]);

//     // Fetch all transactions
//     const fetchAllTransactions = useCallback(async () => {
//         if (!entityId) return;

//         setLoading(true);
//         setError('');

//         try {
//             const endpoints = getApiEndpoints();

//             // Fetch all transaction types in parallel
//             const [invoicesRes, paymentsRes, returnsRes, notesRes] = await Promise.all([
//                 request({
//                     url: endpoints.invoices,
//                     method: 'get',
//                     params: { [entityType === 'party' ? 'partyId' : 'vendorId']: entityId, gstType, limit: 1000 }
//                 }),
//                 request({
//                     url: endpoints.payments,
//                     method: 'get',
//                     params: { [entityType === 'party' ? 'partyId' : 'vendorId']: entityId, gstType, limit: 1000 }
//                 }),
//                 request({
//                     url: endpoints.returns,
//                     method: 'get',
//                     params: { [entityType === 'party' ? 'partyId' : 'vendorId']: entityId, gstType, limit: 1000 }
//                 }),
//                 request({
//                     url: endpoints.notes,
//                     method: 'get',
//                     params: { [entityType === 'party' ? 'partyId' : 'vendorId']: entityId, gstType, limit: 1000 }
//                 })
//             ]);

//             const allTransactions: Transaction[] = [];

//             if (entityType === 'party') {
//                 // Process sales invoices
//                 if (invoicesRes.success && invoicesRes.data) {
//                     invoicesRes.data.forEach((inv: any) => {
//                         allTransactions.push({
//                             id: inv._id,
//                             date: inv.invoiceDate || inv.date,
//                             documentType: 'SALES_INVOICE',
//                             documentNumber: inv.invoiceNumber,
//                             totalAmount: inv.totalAmount || 0,
//                             receivedAmount: inv.receivedAmount || 0,
//                             balanceAmount: inv.balanceAmount || 0,
//                             status: inv.status
//                         } as SalesInvoice);
//                     });
//                 }

//                 // Process payment ins
//                 if (paymentsRes.success && paymentsRes.data) {
//                     paymentsRes.data.forEach((pay: any) => {
//                         allTransactions.push({
//                             id: pay._id,
//                             date: pay.paymentInDate || pay.date,
//                             documentType: 'PAYMENT_IN',
//                             documentNumber: pay.paymentInNumber,
//                             amount: pay.receivedAmount || 0,
//                             paymentType: pay.paymentType
//                         } as PaymentIn);
//                     });
//                 }

//                 // Process sales returns
//                 if (returnsRes.success && returnsRes.data) {
//                     returnsRes.data.forEach((ret: any) => {
//                         allTransactions.push({
//                             id: ret._id,
//                             date: ret.salesReturnDate || ret.date,
//                             documentType: 'SALES_RETURN',
//                             documentNumber: ret.salesReturnNumber,
//                             totalAmount: ret.totalAmount || 0
//                         } as SalesReturn);
//                     });
//                 }

//                 // Process credit notes
//                 if (notesRes.success && notesRes.data) {
//                     notesRes.data.forEach((cn: any) => {
//                         allTransactions.push({
//                             id: cn._id,
//                             date: cn.creditNoteDate || cn.date,
//                             documentType: 'CREDIT_NOTE',
//                             documentNumber: cn.creditNoteNumber,
//                             totalAmount: cn.totalAmount || 0
//                         } as CreditNote);
//                     });
//                 }
//             } else {
//                 // Process purchase invoices
//                 if (invoicesRes.success && invoicesRes.data) {
//                     invoicesRes.data.forEach((inv: any) => {
//                         allTransactions.push({
//                             id: inv._id,
//                             date: inv.purchaseDate || inv.date,
//                             documentType: 'PURCHASE',
//                             documentNumber: inv.purchaseNumber,
//                             totalAmount: inv.totalAmount || 0,
//                             paidAmount: inv.receivedAmount || 0,
//                             balanceAmount: inv.balanceAmount || 0,
//                             status: inv.status
//                         } as PurchaseInvoice);
//                     });
//                 }

//                 // Process payment outs
//                 if (paymentsRes.success && paymentsRes.data) {
//                     paymentsRes.data.forEach((pay: any) => {
//                         allTransactions.push({
//                             id: pay._id,
//                             date: pay.paymentOutDate || pay.date,
//                             documentType: 'PAYMENT_OUT',
//                             documentNumber: pay.paymentOutNumber,
//                             amount: pay.receivedAmount || 0,
//                             paymentType: pay.paymentType
//                         } as PaymentOut);
//                     });
//                 }

//                 // Process purchase returns
//                 if (returnsRes.success && returnsRes.data) {
//                     returnsRes.data.forEach((ret: any) => {
//                         allTransactions.push({
//                             id: ret._id,
//                             date: ret.purchaseReturnDate || ret.date,
//                             documentType: 'PURCHASE_RETURN',
//                             documentNumber: ret.purchaseReturnNumber,
//                             totalAmount: ret.totalAmount || 0
//                         } as PurchaseReturn);
//                     });
//                 }

//                 // Process debit notes
//                 if (notesRes.success && notesRes.data) {
//                     notesRes.data.forEach((dn: any) => {
//                         allTransactions.push({
//                             id: dn._id,
//                             date: dn.debitNoteDate || dn.date,
//                             documentType: 'DEBIT_NOTE',
//                             documentNumber: dn.debitNoteNumber,
//                             totalAmount: dn.totalAmount || 0
//                         } as DebitNote);
//                     });
//                 }
//             }

//             setTransactions(allTransactions);
//             applyFilters(allTransactions);

//         } catch (err: any) {
//             setError(err || 'Failed to fetch transactions');
//             console.error('Error fetching transactions:', err);
//         } finally {
//             setLoading(false);
//         }
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [entityId, entityType, gstType, getApiEndpoints]);

//     // Apply filters when dependencies change
//     useEffect(() => {
//         applyFilters(transactions);
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [debouncedSearch, dateRange, customStartDate, customEndDate, transactions]);

//     // Fetch data when entity changes
//     useEffect(() => {
//         fetchAllTransactions();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [entityId, entityType, gstType]);

//     const getDocumentIcon = (type: string) => {
//         switch (type) {
//             case 'SALES_INVOICE':
//             case 'PURCHASE':
//                 return <Receipt sx={{ fontSize: 16, color: 'primary.main' }} />;
//             case 'PAYMENT_IN':
//             case 'PAYMENT_OUT':
//                 return <Payment sx={{ fontSize: 16, color: 'success.main' }} />;
//             case 'SALES_RETURN':
//             case 'PURCHASE_RETURN':
//                 return <Assignment sx={{ fontSize: 16, color: 'warning.main' }} />;
//             case 'CREDIT_NOTE':
//             case 'DEBIT_NOTE':
//                 return <RequestQuote sx={{ fontSize: 16, color: 'info.main' }} />;
//             default:
//                 return null;
//         }
//     };

//     const getDocumentLabel = (type: string) => {
//         switch (type) {
//             case 'SALES_INVOICE':
//                 return 'Sales Invoice';
//             case 'PURCHASE':
//                 return 'Purchase Invoice';
//             case 'PAYMENT_IN':
//                 return 'Payment In';
//             case 'PAYMENT_OUT':
//                 return 'Payment Out';
//             case 'SALES_RETURN':
//                 return 'Sales Return';
//             case 'PURCHASE_RETURN':
//                 return 'Purchase Return';
//             case 'CREDIT_NOTE':
//                 return 'Credit Note';
//             case 'DEBIT_NOTE':
//                 return 'Debit Note';
//             default:
//                 return type;
//         }
//     };

//     const formatDate = (dateString: string) => {
//         if (!dateString) return '-';
//         return format(new Date(dateString), 'dd/MM/yyyy');
//     };

//     const formatCurrency = (amount: number) => {
//         return new Intl.NumberFormat('en-IN', {
//             style: 'currency',
//             currency: 'INR',
//             minimumFractionDigits: 2,
//             maximumFractionDigits: 2
//         }).format(amount || 0);
//     };



//     return (
//         <Card>
//             <CardContent sx={{ p: 0 }}>
//                 {/* Header with entity info */}
//                 {(entityName || entityContact || entityLocation) && (
//                     <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
//                         <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
//                             {entityName || (entityType === 'party' ? 'Customer' : 'Vendor')}
//                         </Typography>
//                         {entityContact && (
//                             <Typography variant="body2" color="text.secondary">
//                                 Contact: {entityContact}
//                             </Typography>
//                         )}
//                         {entityLocation && (
//                             <Typography variant="body2" color="text.secondary">
//                                 Location: {entityLocation}
//                             </Typography>
//                         )}
//                     </Box>
//                 )}
//                 <Typography variant='h6' sx={{ py: 1, px: 1 }}>{ledgerLabel}</Typography>

//                 {/* Filters Section */}
//                 <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'space-between', minWidth: 300, py: 2, px: 2 }}>
//                     <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
//                         <TextField
//                             placeholder="Search by document number..."
//                             value={searchTerm}
//                             onChange={(e) => setSearchTerm(e.target.value)}
//                             size="small"
//                             sx={{ minWidth: 250 }}
//                             InputProps={{
//                                 startAdornment: (
//                                     <InputAdornment position="start">
//                                         <Search fontSize="small" />
//                                     </InputAdornment>
//                                 ),
//                             }}
//                         />

//                         <FormControl size="small" sx={{ minWidth: 140 }}>
//                             <InputLabel>Date Range</InputLabel>
//                             <Select
//                                 value={dateRange}
//                                 label="Date Range"
//                                 onChange={(e: SelectChangeEvent) => {
//                                     const value = e.target.value;
//                                     setDateRange(value);
//                                     // Reset custom dates when switching away from custom
//                                     if (value !== 'custom') {
//                                         setCustomStartDate(null);
//                                         setCustomEndDate(null);
//                                     }
//                                 }}
//                             >
//                                 <MenuItem value="all">All Time</MenuItem>
//                                 <MenuItem value="3months">Last 3 Months</MenuItem>
//                                 <MenuItem value="6months">Last 6 Months</MenuItem>
//                                 <MenuItem value="1year">Last 1 Year</MenuItem>
//                                 <MenuItem value="custom">Custom Range</MenuItem>
//                             </Select>
//                         </FormControl>

//                         {/* Show date pickers when custom is selected */}
//                         {dateRange === 'custom' && (
//                             <LocalizationProvider dateAdapter={AdapterDateFns}>
//                                 <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
//                                     <DatePicker
//                                         label="From"
//                                         value={customStartDate}
//                                         onChange={(newValue) => {
//                                             setCustomStartDate(newValue as any);
//                                         }}
//                                         slotProps={{
//                                             textField: {
//                                                 size: 'small',
//                                                 sx: { width: 140 }
//                                             }
//                                         }}
//                                     />
//                                     <DatePicker
//                                         label="To"
//                                         value={customEndDate}
//                                         onChange={(newValue) => {
//                                             setCustomEndDate(newValue as any);
//                                         }}
//                                         minDate={customStartDate || undefined}
//                                         slotProps={{
//                                             textField: {
//                                                 size: 'small',
//                                                 sx: { width: 140 }
//                                             }
//                                         }}
//                                     />
//                                 </Box>
//                             </LocalizationProvider>
//                         )}

//                         {/* Show chip when both dates are selected */}
//                         {dateRange === 'custom' && customStartDate && customEndDate && (
//                             <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                                 <Chip
//                                     icon={<CalendarToday />}
//                                     label={`${formatDate(customStartDate.toISOString())} - ${formatDate(customEndDate.toISOString())}`}
//                                     onDelete={() => {
//                                         setDateRange('all');
//                                         setCustomStartDate(null);
//                                         setCustomEndDate(null);
//                                     }}
//                                     color="primary"
//                                     variant="outlined"
//                                     size="small"
//                                 />
//                             </Box>
//                         )}
//                     </Box>

//                     {/* Balance Summary */}
//                     <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
//                         <Box>
//                             <Typography variant="body2" color="text.secondary">
//                                 {totals.balance >= 0
//                                     ? (entityType === 'party' ? 'Receivable Amount' : 'Payable Amount')
//                                     : (entityType === 'party' ? 'Payable Amount' : 'Receivable Amount')
//                                 }
//                             </Typography>
//                             <Typography
//                                 variant="h6"
//                                 color={totals.balance >= 0 ? 'success.main' : 'error.main'}
//                                 sx={{ fontWeight: 'bold' }}
//                             >
//                                 {formatCurrency(Math.abs(totals.balance))}
//                                 <Typography component="span" variant="caption" sx={{ ml: 1 }}>
//                                     {entityType === 'party'
//                                         ? (totals.balance >= 0 ? '(Dr)' : '(Cr)')
//                                         : (totals.balance >= 0 ? '(Cr)' : '(Dr)')
//                                     }
//                                 </Typography>
//                             </Typography>
//                         </Box>
//                     </Box>
//                 </Box>

//                 {/* Totals Summary Cards */}
//                 {/* <Box sx={{ display: 'flex', gap: 2, mb: 2, px: 2 }}>
//                     <Paper sx={{ flex: 1, p: 1.5, bgcolor: 'error.light', color: 'error.contrastText' }}>
//                         <Typography variant="caption">Total Debit</Typography>
//                         <Typography variant="h6" fontWeight="bold">
//                             {formatCurrency(totals.totalDebit)}
//                         </Typography>
//                     </Paper>
//                     <Paper sx={{ flex: 1, p: 1.5, bgcolor: 'success.light', color: 'success.contrastText' }}>
//                         <Typography variant="caption">Total Credit</Typography>
//                         <Typography variant="h6" fontWeight="bold">
//                             {formatCurrency(totals.totalCredit)}
//                         </Typography>
//                     </Paper>
//                 </Box> */}

//                 {/* Table */}
//                 <TableContainer component={Paper} elevation={0}>
//                     <Table size="small">
//                         <TableHead sx={{ bgcolor: 'grey.50' }}>
//                             <TableRow>
//                                 <TableCell><strong>Date</strong></TableCell>
//                                 <TableCell><strong>Document Type</strong></TableCell>
//                                 <TableCell><strong>Document No.</strong></TableCell>
//                                 <TableCell align="right"><strong>Debit (₹)</strong></TableCell>
//                                 <TableCell align="right"><strong>Credit (₹)</strong></TableCell>
//                                 <TableCell align="right"><strong>Balance (₹)</strong></TableCell>
//                                 {/* <TableCell><strong>Status/Notes</strong></TableCell> */}
//                             </TableRow>
//                         </TableHead>
//                         <TableBody>
//                             {loading ? (
//                                 <TableRow>
//                                     <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
//                                         <CircularProgress size={24} />
//                                         <Typography variant="body2" sx={{ mt: 1 }}>
//                                             Loading transactions...
//                                         </Typography>
//                                     </TableCell>
//                                 </TableRow>
//                             ) : error ? (
//                                 <TableRow>
//                                     <TableCell colSpan={7} align="center">
//                                         <Alert severity="error">{error}</Alert>
//                                     </TableCell>
//                                 </TableRow>
//                             ) : filteredTransactions.length === 0 ? (
//                                 <TableRow>
//                                     <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
//                                         <Typography variant="body2" color="text.secondary">
//                                             No transactions found
//                                         </Typography>
//                                     </TableCell>
//                                 </TableRow>
//                             ) : (
//                                 filteredTransactions.map((transaction) => (
//                                     <TableRow key={`${transaction.documentType}-${transaction.id}`} hover>
//                                         <TableCell>
//                                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                 <FiberManualRecord
//                                                     sx={{
//                                                         fontSize: 10,
//                                                         color: transaction.status === 'PAID' ? 'success.main' :
//                                                             transaction.status === 'PARTIAL_PAID' ? 'warning.main' : 'action.disabled'
//                                                     }}
//                                                 />
//                                                 {formatDate(transaction.date)}
//                                             </Box>
//                                         </TableCell>
//                                         <TableCell>
//                                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                 {getDocumentIcon(transaction.documentType)}
//                                                 <Typography variant="body2">
//                                                     {getDocumentLabel(transaction.documentType)}
//                                                 </Typography>
//                                             </Box>
//                                         </TableCell>
//                                         <TableCell>
//                                             <Chip
//                                                 label={transaction.documentNumber}
//                                                 size="small"
//                                                 variant="outlined"
//                                                 sx={{ fontSize: '0.7rem' }}
//                                             />
//                                         </TableCell>
//                                         <TableCell align="right" sx={{ fontWeight: 500 }}>
//                                             {getDebitAmount(transaction) > 0 ? formatCurrency(getDebitAmount(transaction)) : '-'}
//                                         </TableCell>
//                                         <TableCell align="right" sx={{ fontWeight: 500 }}>
//                                             {getCreditAmount(transaction) > 0 ? formatCurrency(getCreditAmount(transaction)) : '-'}
//                                         </TableCell>
//                                         <TableCell
//                                             align="right"
//                                             sx={{
//                                                 color: transaction.runningBalance >= 0 ? 'success.main' : 'error.main',
//                                                 fontWeight: 'bold'
//                                             }}
//                                         >
//                                             {formatCurrency(Math.abs(transaction.runningBalance || 0))}
//                                             <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
//                                                 {entityType === 'party'
//                                                     ? (transaction.runningBalance >= 0 ? '(Dr)' : '(Cr)')
//                                                     : (transaction.runningBalance >= 0 ? '(Cr)' : '(Dr)')
//                                                 }
//                                             </Typography>
//                                         </TableCell>
//                                         {/* <TableCell>
//                                             {(transaction.documentType === 'SALES_INVOICE' || transaction.documentType === 'PURCHASE') && (
//                                                 <Tooltip title={`Balance: ${formatCurrency((transaction as any).balanceAmount)}`}>
//                                                     <Chip
//                                                         label={(transaction as any).status}
//                                                         size="small"
//                                                         color={
//                                                             (transaction as any).status === 'PAID' ? 'success' :
//                                                                 (transaction as any).status === 'PARTIAL_PAID' ? 'warning' :
//                                                                     (transaction as any).status === 'OVERPAID' ? 'info' : 'default'
//                                                         }
//                                                         sx={{ fontSize: '0.7rem', height: 20 }}
//                                                     />
//                                                 </Tooltip>
//                                             )}
//                                             {(transaction.documentType === 'PAYMENT_IN' || transaction.documentType === 'PAYMENT_OUT') && (
//                                                 <Chip
//                                                     label={(transaction as any).paymentType}
//                                                     size="small"
//                                                     variant="outlined"
//                                                     sx={{ fontSize: '0.7rem', height: 20 }}
//                                                 />
//                                             )}
//                                         </TableCell> */}
//                                     </TableRow>
//                                 ))
//                             )}
//                         </TableBody>
//                     </Table>
//                 </TableContainer>
//             </CardContent>
//         </Card>
//     );
// };

// export default CommonLedgerTable;

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Chip,
    CircularProgress,
    Alert,
    Card,
    CardContent,
} from '@mui/material';
import { Search, Receipt, Payment, Assignment, RequestQuote, FiberManualRecord, CalendarToday } from '@mui/icons-material';
import { format } from 'date-fns';
import { request } from '../../utils/request';
import { useDebounce } from '../../utils/useDebounce';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Types for combined transactions with running balance
interface BaseTransaction {
    id: string;
    date: string;
    documentType: 'SALES_INVOICE' | 'PURCHASE' | 'PAYMENT_IN' | 'PAYMENT_OUT' | 'SALES_RETURN' | 'PURCHASE_RETURN' | 'CREDIT_NOTE' | 'DEBIT_NOTE';
    documentNumber: string;
    amount: number;
    status?: string;
    paymentType?: string;
    receivedAmount?: number;
    paidAmount?: number;
    totalAmount?: number;
    runningBalance?: number;
}

interface SalesInvoice extends BaseTransaction {
    documentType: 'SALES_INVOICE';
    totalAmount: number;
    receivedAmount: number;
    balanceAmount: number;
}

interface PurchaseInvoice extends BaseTransaction {
    documentType: 'PURCHASE';
    totalAmount: number;
    paidAmount: number;
    balanceAmount: number;
}

interface PaymentIn extends BaseTransaction {
    documentType: 'PAYMENT_IN';
    amount: number;
    paymentType: string;
}

interface PaymentOut extends BaseTransaction {
    documentType: 'PAYMENT_OUT';
    amount: number;
    paymentType: string;
}

interface SalesReturn extends BaseTransaction {
    documentType: 'SALES_RETURN';
    totalAmount: number;
}

interface PurchaseReturn extends BaseTransaction {
    documentType: 'PURCHASE_RETURN';
    totalAmount: number;
}

interface CreditNote extends BaseTransaction {
    documentType: 'CREDIT_NOTE';
    totalAmount: number;
}

interface DebitNote extends BaseTransaction {
    documentType: 'DEBIT_NOTE';
    totalAmount: number;
}

type Transaction = SalesInvoice | PurchaseInvoice | PaymentIn | PaymentOut | SalesReturn | PurchaseReturn | CreditNote | DebitNote;

type TransactionWithBalance = Transaction & {
    runningBalance: number;
};

interface LedgerTableProps {
    entityId: string;
    entityType?: 'party' | 'vendor';
    gstType?: 'GST' | 'NON-GST';
    entityName?: string;
    entityContact?: string;
    entityLocation?: string;
    ledgerLabel?: string;
    isPurchase?: boolean; // Add this prop to differentiate between sales and purchase
    onTotalChange?: (totalDebit: number, totalCredit: number, balance: number) => void;
}

const CommonLedgerTable: React.FC<LedgerTableProps> = ({
    entityId,
    entityType,
    gstType = 'GST',
    entityName,
    entityContact,
    entityLocation,
    ledgerLabel,
    isPurchase = false, // Default to false (sales mode)
    onTotalChange
}) => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [filteredTransactions, setFilteredTransactions] = useState<TransactionWithBalance[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [dateRange, setDateRange] = useState<string>('all');
    const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
    const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

    // Totals
    const [totals, setTotals] = useState({
        totalDebit: 0,
        totalCredit: 0,
        balance: 0
    });

    // Get API endpoints based on entity type and isPurchase flag
    const getApiEndpoints = useCallback(() => {
        if (isPurchase) {
            // Purchase mode - use purchase endpoints
            return {
                invoices: 'purchase/get-all-purchase-invoices',
                payments: 'paymentOut/get-all-payment-outs',
                returns: 'purchaseReturn/get-all-purchase-returns',
                notes: 'debitNote/get-all-debit-notes'
            };
        } else {
            // Sales mode - use sales endpoints
            return {
                invoices: 'invoice/get-all-sales-invoices',
                payments: 'paymentIn/get-all-payment-ins',
                returns: 'salesReturn/get-all-sales-returns',
                notes: 'creditNote/get-all-credit-notes'
            };
        }
    }, [isPurchase]);

    // Get the appropriate parameter name for API calls
    const getParamName = useCallback(() => {
        if (isPurchase) {
            // For purchase, we need to handle both party and vendor
            // The parent component should pass the correct entityId
            return entityType === 'party' ? 'partyId' : 'vendorId';// or 'partyId' - this depends on what you're passing
        } else {
            // For sales, always use partyId
            return 'partyId';
        }
    }, [isPurchase, entityType]);

    const getDebitAmount = useCallback((transaction: Transaction): number => {
        if (!isPurchase) { // Sales mode
            switch (transaction.documentType) {
                case 'SALES_INVOICE':
                    return (transaction as SalesInvoice).totalAmount || 0;
                default:
                    return 0;
            }
        } else { // Purchase mode
            switch (transaction.documentType) {
                case 'PURCHASE':
                    return (transaction as PurchaseInvoice).paidAmount || 0;
                case 'PAYMENT_OUT':
                    return (transaction as PaymentOut).amount || 0;
                case 'PURCHASE_RETURN':
                case 'DEBIT_NOTE':
                    return (transaction as PurchaseReturn | DebitNote).totalAmount || 0;
                default:
                    return 0;
            }
        }
    }, [isPurchase]);

    const getCreditAmount = useCallback((transaction: Transaction): number => {
        if (!isPurchase) { // Sales mode
            switch (transaction.documentType) {
                case 'SALES_INVOICE':
                    return (transaction as SalesInvoice).receivedAmount || 0;
                case 'PAYMENT_IN':
                    return (transaction as PaymentIn).amount || 0;
                case 'SALES_RETURN':
                case 'CREDIT_NOTE':
                    return (transaction as SalesReturn | CreditNote).totalAmount || 0;
                default:
                    return 0;
            }
        } else { // Purchase mode
            switch (transaction.documentType) {
                case 'PURCHASE':
                    return (transaction as PurchaseInvoice).totalAmount || 0;
                default:
                    return 0;
            }
        }
    }, [isPurchase]);

    // Calculate running balance
    const calculateRunningBalance = useCallback((transactions: Transaction[]): TransactionWithBalance[] => {
        let balance = 0;

        // Sort by date (oldest first) for running balance calculation
        const sorted = [...transactions].sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        return sorted.map(transaction => {
            if (!isPurchase) {
                // For sales: Debit increases balance, Credit decreases balance
                const debitAmount = getDebitAmount(transaction);
                const creditAmount = getCreditAmount(transaction);
                balance = balance + debitAmount - creditAmount;
            } else {
                // For purchases: Credit increases balance (we owe), Debit decreases balance (we paid)
                const debitAmount = getDebitAmount(transaction);
                const creditAmount = getCreditAmount(transaction);
                balance = balance + creditAmount - debitAmount;
            }

            return {
                ...transaction,
                runningBalance: balance
            };
        });
    }, [isPurchase, getDebitAmount, getCreditAmount]);

    // Calculate totals
    const calculateTotals = useCallback((data: Transaction[]) => {
        let totalDebit = 0;
        let totalCredit = 0;

        data.forEach(t => {
            if (!isPurchase) {
                // Sales mode (customer perspective)
                switch (t.documentType) {
                    case 'SALES_INVOICE':
                        totalDebit += (t as SalesInvoice).totalAmount || 0;
                        totalCredit += (t as SalesInvoice).receivedAmount || 0;
                        break;
                    case 'PAYMENT_IN':
                        totalCredit += (t as PaymentIn).amount || 0;
                        break;
                    case 'SALES_RETURN':
                    case 'CREDIT_NOTE':
                        totalCredit += (t as SalesReturn | CreditNote).totalAmount || 0;
                        break;
                }
            } else {
                // Purchase mode (supplier perspective)
                switch (t.documentType) {
                    case 'PURCHASE':
                        totalCredit += (t as PurchaseInvoice).totalAmount || 0;
                        totalDebit += (t as PurchaseInvoice).paidAmount || 0;
                        break;
                    case 'PAYMENT_OUT':
                        totalDebit += (t as PaymentOut).amount || 0;
                        break;
                    case 'PURCHASE_RETURN':
                    case 'DEBIT_NOTE':
                        totalDebit += (t as PurchaseReturn | DebitNote).totalAmount || 0;
                        break;
                }
            }
        });

        const balance = !isPurchase
            ? totalDebit - totalCredit  // Sales: positive = customer owes us
            : totalCredit - totalDebit; // Purchase: positive = we owe vendor

        setTotals({ totalDebit, totalCredit, balance });

        if (onTotalChange) {
            onTotalChange(totalDebit, totalCredit, balance);
        }
    }, [isPurchase, onTotalChange]);

    // Apply filters (search and date range) and calculate running balance
    const applyFilters = useCallback((data: Transaction[]) => {
        let filtered = [...data];

        // Apply search filter
        if (debouncedSearch) {
            const search = debouncedSearch.toLowerCase();
            filtered = filtered.filter(t =>
                t.documentNumber?.toLowerCase().includes(search) ||
                t.documentType.toLowerCase().includes(search)
            );
        }

        // Apply date range filter
        if (dateRange !== 'all') {
            const now = new Date();
            let startDate = new Date();

            if (dateRange === 'custom' && customStartDate && customEndDate) {
                startDate = new Date(customStartDate);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(customEndDate);
                endDate.setHours(23, 59, 59, 999);

                filtered = filtered.filter(t => {
                    const tDate = new Date(t.date);
                    return tDate >= startDate && tDate <= endDate;
                });
            } else {
                switch (dateRange) {
                    case '3months':
                        startDate.setMonth(now.getMonth() - 3);
                        break;
                    case '6months':
                        startDate.setMonth(now.getMonth() - 6);
                        break;
                    case '1year':
                        startDate.setFullYear(now.getFullYear() - 1);
                        break;
                    default:
                        break;
                }

                if (dateRange !== 'all' && dateRange !== 'custom') {
                    filtered = filtered.filter(t => new Date(t.date) >= startDate);
                }
            }
        }

        // Calculate running balance
        const withBalance = calculateRunningBalance(filtered);
        setFilteredTransactions(withBalance);
        calculateTotals(filtered);
    }, [debouncedSearch, dateRange, customStartDate, customEndDate, calculateRunningBalance, calculateTotals]);

    // Fetch all transactions
    const fetchAllTransactions = useCallback(async () => {
        if (!entityId) return;

        setLoading(true);
        setError('');

        try {
            const endpoints = getApiEndpoints();
            const paramName = getParamName();

            // Fetch all transaction types in parallel
            const [invoicesRes, paymentsRes, returnsRes, notesRes] = await Promise.all([
                request({
                    url: endpoints.invoices,
                    method: 'get',
                    params: { [paramName]: entityId, gstType, limit: 1000 }
                }),
                request({
                    url: endpoints.payments,
                    method: 'get',
                    params: { [paramName]: entityId, gstType, limit: 1000 }
                }),
                request({
                    url: endpoints.returns,
                    method: 'get',
                    params: { [paramName]: entityId, gstType, limit: 1000 }
                }),
                request({
                    url: endpoints.notes,
                    method: 'get',
                    params: { [paramName]: entityId, gstType, limit: 1000 }
                })
            ]);

            const allTransactions: Transaction[] = [];

            if (!isPurchase) {
                // Process sales mode transactions
                // Process sales invoices
                if (invoicesRes.success && invoicesRes.data) {
                    invoicesRes.data.forEach((inv: any) => {
                        allTransactions.push({
                            id: inv._id,
                            date: inv.invoiceDate || inv.date,
                            documentType: 'SALES_INVOICE',
                            documentNumber: inv.invoiceNumber,
                            totalAmount: inv.totalAmount || 0,
                            receivedAmount: inv.receivedAmount || 0,
                            balanceAmount: inv.balanceAmount || 0,
                            status: inv.status
                        } as SalesInvoice);
                    });
                }

                // Process payment ins
                if (paymentsRes.success && paymentsRes.data) {
                    paymentsRes.data.forEach((pay: any) => {
                        allTransactions.push({
                            id: pay._id,
                            date: pay.paymentInDate || pay.date,
                            documentType: 'PAYMENT_IN',
                            documentNumber: pay.paymentInNumber,
                            amount: pay.receivedAmount || 0,
                            paymentType: pay.paymentType
                        } as PaymentIn);
                    });
                }

                // Process sales returns
                if (returnsRes.success && returnsRes.data) {
                    returnsRes.data.forEach((ret: any) => {
                        allTransactions.push({
                            id: ret._id,
                            date: ret.salesReturnDate || ret.date,
                            documentType: 'SALES_RETURN',
                            documentNumber: ret.salesReturnNumber,
                            totalAmount: ret.totalAmount || 0
                        } as SalesReturn);
                    });
                }

                // Process credit notes
                if (notesRes.success && notesRes.data) {
                    notesRes.data.forEach((cn: any) => {
                        allTransactions.push({
                            id: cn._id,
                            date: cn.creditNoteDate || cn.date,
                            documentType: 'CREDIT_NOTE',
                            documentNumber: cn.creditNoteNumber,
                            totalAmount: cn.totalAmount || 0
                        } as CreditNote);
                    });
                }
            } else {
                // Process purchase mode transactions
                // Process purchase invoices
                if (invoicesRes.success && invoicesRes.data) {
                    invoicesRes.data.forEach((inv: any) => {
                        allTransactions.push({
                            id: inv._id,
                            date: inv.purchaseDate || inv.date,
                            documentType: 'PURCHASE',
                            documentNumber: inv.purchaseNumber,
                            totalAmount: inv.totalAmount || 0,
                            paidAmount: inv.receivedAmount || 0,
                            balanceAmount: inv.balanceAmount || 0,
                            status: inv.status
                        } as PurchaseInvoice);
                    });
                }

                // Process payment outs
                if (paymentsRes.success && paymentsRes.data) {
                    paymentsRes.data.forEach((pay: any) => {
                        allTransactions.push({
                            id: pay._id,
                            date: pay.paymentOutDate || pay.date,
                            documentType: 'PAYMENT_OUT',
                            documentNumber: pay.paymentOutNumber,
                            amount: pay.receivedAmount || 0,
                            paymentType: pay.paymentType
                        } as PaymentOut);
                    });
                }

                // Process purchase returns
                if (returnsRes.success && returnsRes.data) {
                    returnsRes.data.forEach((ret: any) => {
                        allTransactions.push({
                            id: ret._id,
                            date: ret.purchaseReturnDate || ret.date,
                            documentType: 'PURCHASE_RETURN',
                            documentNumber: ret.purchaseReturnNumber,
                            totalAmount: ret.totalAmount || 0
                        } as PurchaseReturn);
                    });
                }

                // Process debit notes
                if (notesRes.success && notesRes.data) {
                    notesRes.data.forEach((dn: any) => {
                        allTransactions.push({
                            id: dn._id,
                            date: dn.debitNoteDate || dn.date,
                            documentType: 'DEBIT_NOTE',
                            documentNumber: dn.debitNoteNumber,
                            totalAmount: dn.totalAmount || 0
                        } as DebitNote);
                    });
                }
            }

            setTransactions(allTransactions);
            applyFilters(allTransactions);

        } catch (err: any) {
            setError(err || 'Failed to fetch transactions');
            console.error('Error fetching transactions:', err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, gstType, getApiEndpoints, getParamName, isPurchase]);

    // Apply filters when dependencies change
    useEffect(() => {
        applyFilters(transactions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, dateRange, customStartDate, customEndDate, transactions]);

    // Fetch data when entity changes
    useEffect(() => {
        fetchAllTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, gstType, isPurchase]);

    const getDocumentIcon = (type: string) => {
        switch (type) {
            case 'SALES_INVOICE':
            case 'PURCHASE':
                return <Receipt sx={{ fontSize: 16, color: 'primary.main' }} />;
            case 'PAYMENT_IN':
            case 'PAYMENT_OUT':
                return <Payment sx={{ fontSize: 16, color: 'success.main' }} />;
            case 'SALES_RETURN':
            case 'PURCHASE_RETURN':
                return <Assignment sx={{ fontSize: 16, color: 'warning.main' }} />;
            case 'CREDIT_NOTE':
            case 'DEBIT_NOTE':
                return <RequestQuote sx={{ fontSize: 16, color: 'info.main' }} />;
            default:
                return null;
        }
    };

    const getDocumentLabel = (type: string) => {
        switch (type) {
            case 'SALES_INVOICE':
                return 'Sales Invoice';
            case 'PURCHASE':
                return 'Purchase Invoice';
            case 'PAYMENT_IN':
                return 'Payment In';
            case 'PAYMENT_OUT':
                return 'Payment Out';
            case 'SALES_RETURN':
                return 'Sales Return';
            case 'PURCHASE_RETURN':
                return 'Purchase Return';
            case 'CREDIT_NOTE':
                return 'Credit Note';
            case 'DEBIT_NOTE':
                return 'Debit Note';
            default:
                return type;
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return format(new Date(dateString), 'dd/MM/yyyy');
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <Card>
            <CardContent sx={{ p: 0 }}>
                {/* Header with entity info */}
                {(entityName || entityContact || entityLocation) && (
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {entityName || (isPurchase ? 'Vendor' : 'Customer')}
                        </Typography>
                        {entityContact && (
                            <Typography variant="body2" color="text.secondary">
                                Contact: {entityContact}
                            </Typography>
                        )}
                        {entityLocation && (
                            <Typography variant="body2" color="text.secondary">
                                Location: {entityLocation}
                            </Typography>
                        )}
                    </Box>
                )}
                <Typography variant='h6' sx={{ py: 1, px: 1 }}>{ledgerLabel}</Typography>

                {/* Filters Section */}
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'space-between', minWidth: 300, py: 2, px: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Search by document number..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            sx={{ minWidth: 250 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Date Range</InputLabel>
                            <Select
                                value={dateRange}
                                label="Date Range"
                                onChange={(e: SelectChangeEvent) => {
                                    const value = e.target.value;
                                    setDateRange(value);
                                    if (value !== 'custom') {
                                        setCustomStartDate(null);
                                        setCustomEndDate(null);
                                    }
                                }}
                            >
                                <MenuItem value="all">All Time</MenuItem>
                                <MenuItem value="3months">Last 3 Months</MenuItem>
                                <MenuItem value="6months">Last 6 Months</MenuItem>
                                <MenuItem value="1year">Last 1 Year</MenuItem>
                                <MenuItem value="custom">Custom Range</MenuItem>
                            </Select>
                        </FormControl>

                        {dateRange === 'custom' && (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <DatePicker
                                        label="From"
                                        value={customStartDate}
                                        onChange={(newValue) => {
                                            setCustomStartDate(newValue as any);
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { width: 140 }
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="To"
                                        value={customEndDate}
                                        onChange={(newValue) => {
                                            setCustomEndDate(newValue as any);
                                        }}
                                        minDate={customStartDate || undefined}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { width: 140 }
                                            }
                                        }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        )}

                        {dateRange === 'custom' && customStartDate && customEndDate && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    icon={<CalendarToday />}
                                    label={`${formatDate(customStartDate.toISOString())} - ${formatDate(customEndDate.toISOString())}`}
                                    onDelete={() => {
                                        setDateRange('all');
                                        setCustomStartDate(null);
                                        setCustomEndDate(null);
                                    }}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Balance Summary */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {totals.balance >= 0
                                    ? (!isPurchase ? 'Receivable Amount' : 'Payable Amount')
                                    : (!isPurchase ? 'Payable Amount' : 'Receivable Amount')
                                }
                            </Typography>
                            <Typography
                                variant="h6"
                                color={totals.balance >= 0 ? 'success.main' : 'error.main'}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {formatCurrency(Math.abs(totals.balance))}
                                <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                                    {!isPurchase
                                        ? (totals.balance >= 0 ? '(Dr)' : '(Cr)')
                                        : (totals.balance >= 0 ? '(Cr)' : '(Dr)')
                                    }
                                </Typography>
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Table */}
                <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Document Type</strong></TableCell>
                                <TableCell><strong>Document No.</strong></TableCell>
                                <TableCell align="right"><strong>Debit (₹)</strong></TableCell>
                                <TableCell align="right"><strong>Credit (₹)</strong></TableCell>
                                <TableCell align="right"><strong>Balance (₹)</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading transactions...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Alert severity="error">{error}</Alert>
                                    </TableCell>
                                </TableRow>
                            ) : filteredTransactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No transactions found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredTransactions.map((transaction) => (
                                    <TableRow key={`${transaction.documentType}-${transaction.id}`} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <FiberManualRecord
                                                    sx={{
                                                        fontSize: 10,
                                                        color: transaction.status === 'PAID' ? 'success.main' :
                                                            transaction.status === 'PARTIAL_PAID' ? 'warning.main' : 'action.disabled'
                                                    }}
                                                />
                                                {formatDate(transaction.date)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getDocumentIcon(transaction.documentType)}
                                                <Typography variant="body2">
                                                    {getDocumentLabel(transaction.documentType)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={transaction.documentNumber}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                            {getDebitAmount(transaction) > 0 ? formatCurrency(getDebitAmount(transaction)) : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                            {getCreditAmount(transaction) > 0 ? formatCurrency(getCreditAmount(transaction)) : '-'}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                color: transaction.runningBalance >= 0 ? 'success.main' : 'error.main',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {formatCurrency(Math.abs(transaction.runningBalance || 0))}
                                            <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                                                {!isPurchase
                                                    ? (transaction.runningBalance >= 0 ? '(Dr)' : '(Cr)')
                                                    : (transaction.runningBalance >= 0 ? '(Cr)' : '(Dr)')
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default CommonLedgerTable;